import type { LucideIcon } from 'lucide-react'
import { MailIcon, MapPinnedIcon } from 'lucide-react'

/**
 * VRG-591 — the two "Certificate access" flags, named once.
 *
 * The same pair is drawn on four surfaces: the Settings -> Features toggles, the
 * product detail row, the certificate detail row, and the edit modal. A flag that
 * is "Require email to view" in one place and "Email gate" in another stops being
 * a recognisable thing, so the wording, the glyph and the consequence sentence all
 * live here rather than being retyped per call site (§36, §44's single-definition
 * argument applied to UI copy).
 *
 * ## The two flags are now PARALLEL, and that is a behaviour change, not a rename
 *
 * `collect_location` used to be called "Collect location" and used to mean it: the
 * app asked, and allow / deny / timeout all fell through to the certificate anyway,
 * so the setting bought a prompt and nothing else. It is now a HARD GATE on the
 * certificate's DETAILS in exactly the way the email requirement is — a visitor who
 * declines, or whose device returns no fix, does not get past it (see
 * `RequestAccessInfo`'s `collectLocation` prop in apps/app for the enforcement).
 *
 * So the label is "Require a location to view", matching "Require email to view",
 * and both flags read their resolved state through ONE pair of words — "Required" /
 * "Not required" — rather than four bespoke phrases ("Email required", "Location
 * collected", …). An operator scanning a product page reads one pattern twice
 * instead of learning two vocabularies for one idea.
 *
 * ⚠️ IF THE ENFORCEMENT IS EVER RELAXED, THIS COPY BECOMES A LIE. The label and the
 * consequence sentences below assert a gate. They were false for `collect_location`
 * before the enforcement landed, and naming a thing for what it is about to do is
 * only defensible while the two stay in step.
 *
 * The `consequence` strings are the ones the confirmation modal states, and they
 * are written per DIRECTION because turning a requirement on and turning it off have
 * different costs and only one of them is about the visitor. Naming the effect is
 * the whole reason that modal exists rather than a Save button (§43).
 */
export type AccessSettingKey = 'collect_email' | 'collect_location'

/**
 * Where a copy string is being rendered. `company` is the Settings -> Features
 * card, which used to hand-write its own labels and consequences — four strings
 * that had already drifted from the ones here (it still described location as
 * "with their consent" after the gate landed). Widening the scope union is what
 * lets that page read this file instead, which is the single-source property this
 * module's header claims.
 */
export type AccessSettingScope = 'company' | 'product' | 'certificate'

export type AccessSettingOption = {
  key: AccessSettingKey
  /** Matrix-short label for the info row and the Features toggle. */
  label: string
  /** One line under the Features toggle, saying what the visitor experiences. */
  description: string
  /** Glyph, carried here so all four surfaces agree by construction. */
  icon: LucideIcon
  /**
   * How the resolved value reads when ON / OFF.
   *
   * DELIBERATELY IDENTICAL ACROSS BOTH FLAGS — "Required" / "Not required". The
   * previous per-flag phrasings ("Email required" / "Location collected") existed
   * because a row reading "Yes · Company default" does not say yes to WHAT, and
   * that objection is real. It is answered by the `InfoItem` LABEL sitting directly
   * above the value, which now names the subject in both cases ("Require email to
   * view" / "Require a location to view"), so the value only has to carry the
   * boolean. Restating the subject inside the value made two rows that say the same
   * thing look like two different kinds of setting.
   */
  onTitle: string
  offTitle: string
  /**
   * The setting as a NOUN — "Email requirement" — for the modal title and,
   * lower-cased, for mid-sentence use ("Change the email requirement for this
   * product?").
   *
   * Exists because the modals were interpolating `label.toLowerCase()`, and the
   * label is an IMPERATIVE. That produced "Change require email to view for this
   * product?" — already awkward, and outright broken once the location label grew
   * an article ("Change require a location to view …"). One field rather than a
   * separate capitalised and lower-cased pair, so the two cannot drift; the title
   * case is the stored form and every other position lower-cases it.
   *
   * Shape matches `EditActivationTriggerModal`'s "Activation for this product",
   * which is the same modal solving the same problem one row up.
   */
  noun: string
  /** Confirm-button verb phrases. §43: a verb naming the action, never "OK". */
  confirmOn: string
  confirmOff: string
  /** What switching it ON does, in the visitor's terms. */
  consequenceOn: (scope: AccessSettingScope) => string
  /** What switching it OFF does, in the brand's terms. */
  consequenceOff: (scope: AccessSettingScope) => string
}

/**
 * The affected set, in two grammatical positions.
 *
 * TWO forms rather than one shared fragment, because a single string cannot sit
 * both mid-sentence ("Anyone opening …") and sentence-initial ("… will open
 * without asking"). The first cut used one fragment plus a
 * `.replace(/^every/, 'Every')`, which produced "Anyone opening every certificate
 * for this product, including ones already issued will have to enter …" at product
 * scope and a lowercase "this certificate will open …" at certificate scope. Both
 * were caught in the browser, not by the typecheck — copy has no compiler.
 *
 * The `plural` form that used to sit here is gone: it existed for the one sentence
 * about scans not reaching the map, and that sentence now uses `subject` like every
 * other OFF-direction consequence.
 */
const affected = {
  /** Mid-sentence, as an object: "Anyone opening <this>". */
  object: (scope: AccessSettingScope) =>
    scope === 'product'
      ? 'a certificate for this product'
      : scope === 'certificate'
        ? 'this certificate'
        : 'one of your public certificates',
  /** Sentence-initial, as a subject: "<This> will open without asking". */
  subject: (scope: AccessSettingScope) =>
    scope === 'product'
      ? 'Every certificate for this product, including ones already issued,'
      : scope === 'certificate'
        ? 'This certificate'
        : 'Your public certificates',
  /**
   * The possessive that AGREES with `subject`, for "… will show <its> details".
   *
   * A third form, added for the same reason the first two exist and caught the same
   * way — in the browser, not by the compiler. `subject` is singular at product and
   * certificate scope ("Every certificate …", "This certificate") but PLURAL at
   * company scope ("Your public certificates"), so one hard-coded "its" produced
   * "Your public certificates will show its details" on the Features page's confirm
   * modal. Whenever a sentence here inflects with the subject, it needs a form of
   * its own rather than a word that happens to fit two of the three cases.
   */
  possessive: (scope: AccessSettingScope) => (scope === 'company' ? 'their' : 'its'),
}

export const ACCESS_SETTINGS: AccessSettingOption[] = [
  {
    key: 'collect_email',
    label: 'Require email to view',
    description: 'Visitors give their email before the certificate details open.',
    icon: MailIcon,
    onTitle: 'Required',
    offTitle: 'Not required',
    noun: 'Email requirement',
    confirmOn: 'Require an email',
    confirmOff: 'Stop requiring an email',
    consequenceOn: scope =>
      `Anyone opening ${affected.object(scope)} will have to give their email ` +
      'address before the details are shown. The product preview stays visible ' +
      'either way. Addresses are stored against the view, so make sure your ' +
      'privacy notice covers it.',
    consequenceOff: scope =>
      `${affected.subject(scope)} will show ${affected.possessive(scope)} details ` +
      'without asking for an email. You stop collecting addresses from these views, ' +
      'which ends email lead capture on them. The ones already collected are kept.',
  },
  {
    key: 'collect_location',
    label: 'Require a location to view',
    description: 'Visitors share their location before the certificate details open.',
    icon: MapPinnedIcon,
    onTitle: 'Required',
    offTitle: 'Not required',
    noun: 'Location requirement',
    confirmOn: 'Require a location',
    confirmOff: 'Stop requiring a location',
    /*
      THIS IS THE SENTENCE THE RENAME RESTS ON. It used to read "will be asked for
      their location, with their consent" — accurate when the setting bought a
      prompt everyone could walk past, and false the moment the gate landed. A
      visitor who says no, or who is stood indoors on cellular and gets no fix, is
      now simply not shown the details. That is a materially bigger thing to switch
      on than "we collect a bit more analytics", and the confirm modal is the only
      place it gets said (§43).
    */
    consequenceOn: scope =>
      `Anyone opening ${affected.object(scope)} will have to share their location ` +
      'before the details are shown. The product preview stays visible either way. ' +
      'A visitor who declines, or whose device cannot get a fix, will not see the ' +
      'details at all. Locations are stored against the scan, so make sure your ' +
      'privacy notice covers it.',
    consequenceOff: scope =>
      `${affected.subject(scope)} will show ${affected.possessive(scope)} details ` +
      'without asking for a location. Scans stop recording where they happened, so ' +
      'new ones will not appear on your scan map. Locations already recorded are kept.',
  },
]

export const accessSettingOption = (key: AccessSettingKey): AccessSettingOption =>
  ACCESS_SETTINGS.find(option => option.key === key) as AccessSettingOption
